import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import {
    AiSettings,
    AiTestRequest,
    AiTestResponse,
    UpdateAiSettingsRequest,
    AiExplanation,
} from '../types';
import toast from 'react-hot-toast';

// Get AI settings
export const useAiSettings = () => {
    return useQuery<AiSettings>({
        queryKey: ['ai-settings'],
        queryFn: async () => {
            const { data } = await api.get<{
                success: boolean;
                data: AiSettings;
            }>('/settings/ai');
            return data.data;
        },
    });
};

// Test AI provider configuration
export const useTestAiProvider = () => {
    return useMutation({
        mutationFn: async (testData: AiTestRequest) => {
            try {
                const { data } = await api.post<{
                    success: boolean;
                    data: AiTestResponse;
                }>('/settings/ai/test', testData);
                return data.data;
            } catch (error: any) {
                // Backend returns 422 for invalid configs
                // Handle this as a test result (not an error to throw)
                if (error.response?.status === 422) {
                    const errorData = error.response.data;

                    // Handle different error formats from backend
                    let message = 'Invalid configuration';

                    if (errorData?.error?.message) {
                        message = errorData.error.message;
                    } else if (errorData?.message) {
                        message = errorData.message;
                    } else if (errorData?.errors) {
                        // Validation errors format
                        message = Object.values(errorData.errors).flat().join(', ');
                    }

                    return {
                        valid: false,
                        provider: testData.provider,
                        message: message,
                    };
                }

                // For other errors, throw to be caught by onError
                throw error;
            }
        },
        onSuccess: (data) => {
            if (data.valid) {
                toast.success(data.message);
            }
            // Don't show error toast here for invalid configs
            // The UI will display the result
        },
        onError: (error: any) => {
            // Only handle unexpected errors (not validation errors)
            const errorMessage =
                error.response?.data?.error?.message ||
                error.response?.data?.message ||
                'Failed to test AI provider configuration.';
            toast.error(errorMessage);
        },
    });
};

// Update AI settings
export const useUpdateAiSettings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (settings: UpdateAiSettingsRequest) => {
            const { data } = await api.put<{
                success: boolean;
                message: string;
                data: { provider: string };
            }>('/settings/ai', settings);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
            toast.success(data.message || 'AI settings updated successfully');
        },
        onError: (error: any) => {
            // Handle different error formats
            let errorMessage = 'Failed to update AI settings.';

            if (error.response?.status === 422) {
                const errorData = error.response.data;

                if (errorData?.error?.message) {
                    errorMessage = errorData.error.message;
                } else if (errorData?.message) {
                    errorMessage = errorData.message;
                } else if (errorData?.errors) {
                    // Validation errors format
                    errorMessage = Object.values(errorData.errors).flat().join(', ');
                }
            } else if (error.response?.data?.error?.message) {
                errorMessage = error.response.data.error.message;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            toast.error(errorMessage);
        },
    });
};

// Get AI explanation for a risk
export const useAiExplanation = (riskId: number, shouldFetch: boolean = true) => {
    return useQuery<AiExplanation>({
        queryKey: ['ai-explanation', riskId],
        queryFn: async () => {
            const { data } = await api.get<{
                success: boolean;
                data: AiExplanation;
            }>(`/dashboard/ai-explanation/${riskId}`);
            return data.data;
        },
        enabled: !!riskId && shouldFetch, // Only fetch when riskId exists AND shouldFetch is true
        retry: false, // Don't retry if plan doesn't support it
    });
};

// Regenerate AI explanation
export const useRegenerateAiExplanation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (riskId: number) => {
            const { data } = await api.post<{
                success: boolean;
                data: AiExplanation;
            }>(`/dashboard/ai-explanation/${riskId}/regenerate`);
            return data.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: ['ai-explanation', data.risk_id],
            });
            toast.success('AI explanation regenerated successfully');
        },
        onError: (error: any) => {
            const errorCode = error.response?.data?.error?.code;
            if (errorCode === 'FEATURE_NOT_AVAILABLE') {
                const errorMessage =
                    error.response?.data?.error?.message ||
                    'AI explanations are only available in Pro and Enterprise plans';
                toast.error(errorMessage);
            } else {
                const errorMessage =
                    error.response?.data?.error?.message ||
                    error.response?.data?.message ||
                    'Failed to regenerate AI explanation.';
                toast.error(errorMessage);
            }
        },
    });
};
